<template>
  <div class="page report-page">
    <div class="page-header report-page-header">
      <h1 class="page-title">Relatório</h1>
      <p class="page-subtitle">Receita e pagamentos</p>
    </div>
    <div>
      <div class="report-period-row">
        <v-select
          :options="periodRangeOptions"
          :reduce="(option) => option.id"
          v-model="periodRange"
          placeholder="Período"
        />
        <template v-if="periodRange === 'custom'">
          <label for="custom-start" class="report-period-label">De</label>
          <input
            id="custom-start"
            v-model="customStartDate"
            type="date"
            class="report-period-field"
            aria-label="Data inicial"
          />
          <label for="custom-end" class="report-period-label">Até</label>
          <input
            id="custom-end"
            v-model="customEndDate"
            type="date"
            class="report-period-field"
            aria-label="Data final"
          />
        </template>
      </div>
      <div v-if="false" class="report-period-row report-backfill-row">
        <button
          type="button"
          class="report-backfill-btn"
          :disabled="backfillLoading"
          @click="runBackfill"
        >
          {{ backfillLoading ? 'Atualizando...' : 'Atualizar dados do relatório' }}
        </button>
        <p v-if="backfillMessage" class="report-backfill-message">{{ backfillMessage }}</p>
      </div>
    </div>

    <div v-if="reportLoading" class="report-loading-skeleton" aria-busy="true" aria-live="polite">
      <p class="report-loading-skeleton-caption">Carregando relatório…</p>
      <div class="report-skeleton-kpis">
        <div class="report-skeleton-card report-skeleton-kpi report-skeleton-block" />
        <div class="report-skeleton-card report-skeleton-kpi report-skeleton-block" />
        <div class="report-skeleton-card report-skeleton-kpi report-skeleton-block" />
        <div class="report-skeleton-card report-skeleton-kpi report-skeleton-block" />
      </div>
      <div class="report-skeleton-chart report-skeleton-block" />
      <div class="report-skeleton-section">
        <div class="report-skeleton-toggle report-skeleton-block" />
        <div class="report-skeleton-table report-skeleton-block" />
      </div>
      <div class="report-skeleton-section">
        <div class="report-skeleton-toggle report-skeleton-block" />
        <div class="report-skeleton-table report-skeleton-block" />
      </div>
    </div>
    <p v-else-if="reportLoadError" class="report-load-error" role="alert">
      {{ reportLoadError }}
    </p>
    <template v-else>
    <ReportKPIs :totals="totals" :format-brl="formatBrl" />

    <ReportChart
      v-if="chartReady"
      :series="series"
      :chart-options="chartOptions"
    />

    <ReportTableSection
      title="Em aberto (por cliente)"
      :is-open="openSections.outstanding"
      :is-empty="outstandingByCustomer.length === 0"
      empty-message="Nenhum atendimento em aberto no período."
      @update:is-open="openSections.outstanding = $event"
    >
      <ReportTable
        :columns="outstandingColumns"
        :rows="outstandingByCustomer"
        :sort-key="sortOutstanding.key"
        :sort-dir="sortOutstanding.dir"
        row-key="clientId"
        :format-cell="formatOutstandingCell"
        @sort="setSort('outstanding', $event)"
      />
    </ReportTableSection>

    <ReportTableSection
      title="Por lash"
      :is-open="openSections.technician"
      :is-empty="revenueByTechnician.length === 0"
      empty-message="Nenhum dado no período."
      @update:is-open="openSections.technician = $event"
    >
      <ReportTable
        :columns="technicianColumns"
        :rows="revenueByTechnician"
        :sort-key="sortTechnician.key"
        :sort-dir="sortTechnician.dir"
        row-key="lashId"
        :format-cell="formatTechnicianCell"
        @sort="setSort('technician', $event)"
      />
    </ReportTableSection>

    <ReportTableSection
      title="Recebido por dia"
      :is-open="openSections.daily"
      :is-empty="dailyValues.length === 0"
      empty-message="Nenhum valor pago no período."
      @update:is-open="openSections.daily = $event"
    >
      <ReportTable
        :columns="dailyColumns"
        :rows="dailyValues"
        row-key="date"
        :format-cell="formatDailyCell"
      />
    </ReportTableSection>

    <ReportTableSection
      title="Pago vs esperado (por cliente)"
      :is-open="openSections.paid"
      :is-empty="customerPaidVsExpected.length === 0"
      empty-message="Nenhum cliente com pagamento no período."
      @update:is-open="openSections.paid = $event"
    >
      <ReportTable
        :columns="paidVsExpectedColumns"
        :rows="customerPaidVsExpected"
        :sort-key="sortPaid.key"
        :sort-dir="sortPaid.dir"
        row-key="clientId"
        :format-cell="formatPaidVsExpectedCell"
        @sort="setSort('paid', $event)"
      />
    </ReportTableSection>

    <ReportTableSection
      title="Por forma de pagamento"
      :is-open="openSections.paymentMethod"
      :is-empty="revenueByPaymentMethod.length === 0"
      empty-message="Nenhum pagamento no período."
      @update:is-open="openSections.paymentMethod = $event"
    >
      <ReportTable
        :columns="paymentMethodColumns"
        :rows="revenueByPaymentMethod"
        :sort-key="sortPaymentMethod.key"
        :sort-dir="sortPaymentMethod.dir"
        row-key="paymentMethodId"
        :format-cell="formatPaymentMethodCell"
        @sort="setSort('paymentMethod', $event)"
      />
    </ReportTableSection>

    <ReportTableSection
      title="Por serviço"
      :is-open="openSections.service"
      :is-empty="revenueByService.length === 0"
      empty-message="Nenhum serviço pago no período."
      @update:is-open="openSections.service = $event"
    >
      <ReportTable
        :columns="serviceColumns"
        :rows="revenueByService"
        :sort-key="sortService.key"
        :sort-dir="sortService.dir"
        row-key="serviceId"
        :format-cell="formatServiceCell"
        @sort="setSort('service', $event)"
      />
    </ReportTableSection>
    </template>
    
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, reactive, watch, onMounted, nextTick } from 'vue';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/firebase';
import { useReportDailyStore } from '@/stores/reportDaily';
import { useAppointmentStore } from '@/stores/appointment';
import { useClientStore } from '@/stores/client';
import { useServiceStore } from '@/stores/service';
import { useUserStore } from '@/stores/user';
import { usePaymentMethodStore } from '@/stores/paymentMethod';
import { mergeReportBuckets, mergedToReportUI } from '@/composables/reportBucketMerge';
import { computePayToProviderByLash } from '@/composables/useProviderPercent';
import ReportKPIs from '@/components/ReportKPIs.vue';
import ReportChart from '@/components/ReportChart.vue';
import ReportTableSection from '@/components/ReportTableSection.vue';
import ReportTable from '@/components/ReportTable.vue';

function formatDateDdMmYy(yyyyMmDd) {
  const parts = String(yyyyMmDd).split('/');
  if (parts.length !== 3) return yyyyMmDd;
  const yy = parts[0].slice(-2);
  return `${parts[2]}/${parts[1]}/${yy}`;
}

function toDateKey(timestamp) {
  const d = new Date(timestamp);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PERIOD_RANGES = {
  'today': null,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '3m': 90 * 24 * 60 * 60 * 1000,
  'year': null,
  'all': null
};

export default {
  name: 'Report',
  components: {
    ReportKPIs,
    ReportChart,
    ReportTableSection,
    ReportTable,
    vSelect
  },
  setup() {
    const reportDailyStore = useReportDailyStore();
    const appointmentStore = useAppointmentStore();
    const clientStore = useClientStore();
    const serviceStore = useServiceStore();
    const userStore = useUserStore();
    const paymentMethodStore = usePaymentMethodStore();

    const periodRange = ref('month');
    const periodRangeOptions = [
      { label: 'Hoje', id: 'today' },
      { label: 'Últimos 7 dias', id: '7d' },
      { label: 'Últimos 30 dias', id: '30d' },
      { label: 'Últimos 3 meses', id: '3m' },
      { label: 'Este mês', id: 'month' },
      { label: 'Este ano', id: 'year' },
      { label: 'Tudo', id: 'all' },
      { label: 'Intervalo personalizado', id: 'custom' }
    ];
    const now = new Date();
    const defaultCustomStart =
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const defaultCustomEnd =
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const customStartDate = ref(defaultCustomStart);
    const customEndDate = ref(defaultCustomEnd);
    const canAccessReport = computed(() => userStore.hasPermission('reports:read'));
    const backfillLoading = ref(false);
    const backfillMessage = ref('');

    async function runBackfill() {
      backfillLoading.value = true;
      backfillMessage.value = '';
      try {
        const functions = getFunctions(app, 'us-central1');
        const backfillReportDaily = httpsCallable(functions, 'backfillReportDaily');
        const res = await backfillReportDaily({ startKey: '2023-03-01' });
        const data = res.data;
        const months = data?.monthsProcessed;
        const days = data?.daysProcessed;
        if (months != null && days != null) {
          backfillMessage.value = `Atualizado: ${months} mês(es) e ${days} dia(s) processado(s).`;
        } else if (data?.daysProcessed != null) {
          backfillMessage.value = `Atualizado: ${data.daysProcessed} dia(s) processado(s).`;
        } else {
          backfillMessage.value = 'Dados do relatório atualizados.';
        }
        setTimeout(() => { backfillMessage.value = ''; }, 5000);
      } catch (err) {
        backfillMessage.value = err?.message || 'Erro ao atualizar dados do relatório.';
      } finally {
        backfillLoading.value = false;
      }
    }

    const openSections = reactive({
      daily: false,
      paid: false,
      outstanding: false,
      service: false,
      technician: false,
      paymentMethod: false
    });

    const sortPaid = ref({ key: 'clientName', dir: 1 });
    const sortOutstanding = ref({ key: 'clientName', dir: 1 });
    const sortService = ref({ key: 'gross', dir: -1 });
    const sortTechnician = ref({ key: 'expected', dir: -1 });
    const sortPaymentMethod = ref({ key: 'gross', dir: -1 });

    function setSort(table, key) {
      const refs = {
        paid: sortPaid,
        outstanding: sortOutstanding,
        service: sortService,
        technician: sortTechnician,
        paymentMethod: sortPaymentMethod,
      };
      const r = refs[table];
      if (r.value.key === key) r.value = { key, dir: -r.value.dir };
      else {
        r.value = {
          key,
          dir: key === 'clientName' || key === 'serviceName' || key === 'lashName' || key === 'paymentMethodName' ? 1 : -1
        };
      }
    }

    function parseDateOnly(ymd) {
      if (!ymd || typeof ymd !== 'string') return null;
      const parts = ymd.trim().split('-').map(Number);
      if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
      const [y, m, d] = parts;
      const date = new Date(y, m - 1, d);
      if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
      return date;
    }

    function startOfDay(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    }

    function endOfDay(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
    }

    const dateRange = computed(() => {
      const nowMs = Date.now();
      const now = new Date();
      const range = periodRange.value;
      if (range === 'all') {
        const start = new Date(2023, 0, 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (range === 'year') {
        const start = new Date(now.getFullYear(), 0, 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (range === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (range === 'today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { min: start.getTime(), max: nowMs };
      }
      if (range === 'custom') {
        const start = parseDateOnly(customStartDate.value);
        const end = parseDateOnly(customEndDate.value);
        if (!start || !end) {
          const fallbackStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const fallbackEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
          return { min: fallbackStart.getTime(), max: fallbackEnd.getTime() };
        }
        const minTs = Math.min(startOfDay(start), startOfDay(end));
        const maxTs = Math.max(endOfDay(start), endOfDay(end));
        return { min: minTs, max: maxTs };
      }
      const ms = PERIOD_RANGES[range];
      return { min: nowMs - ms, max: nowMs };
    });

    const requiredYears = computed(() => {
      const { min, max } = dateRange.value;
      const minY = new Date(min).getFullYear();
      const maxY = new Date(max).getFullYear();
      const years = [];
      for (let y = minY; y <= maxY; y++) years.push(y);
      return years;
    });

    function ensureYearsForPeriod() {
      for (const year of requiredYears.value) {
        reportDailyStore.ensureYearLoaded(year);
      }
      const { min, max } = dateRange.value;
      appointmentStore.ensureMonthsForRange(min, max);
    }

    function getRangeForPreset(preset) {
      const nowMs = Date.now();
      const now = new Date();
      if (preset === 'all') {
        const start = new Date(2023, 0, 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (preset === 'year') {
        const start = new Date(now.getFullYear(), 0, 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (preset === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { min: start.getTime(), max: nowMs };
      }
      if (preset === 'today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { min: start.getTime(), max: nowMs };
      }
      const ms = PERIOD_RANGES[preset];
      if (ms) return { min: nowMs - ms, max: nowMs };
      return { min: nowMs - PERIOD_RANGES['30d'], max: nowMs };
    }

    onMounted(ensureYearsForPeriod);
    watch([periodRange, customStartDate, customEndDate], ensureYearsForPeriod);
    watch(periodRange, (newVal, oldVal) => {
      if (newVal === 'custom' && oldVal && oldVal !== 'custom') {
        const range = getRangeForPreset(oldVal);
        customStartDate.value = toDateKey(range.min);
        customEndDate.value = toDateKey(range.max);
      }
    });

    const reportLoading = computed(() => {
      const bucketsLoading = requiredYears.value.some((y) =>
        reportDailyStore.loadingYears.includes(String(y))
      );
      const { min, max } = dateRange.value;
      const appointmentsLoading = appointmentStore.isAppointmentsLoadingForRange(min, max);
      return bucketsLoading || appointmentsLoading;
    });

    const reportLoadError = computed(() => reportDailyStore.loadError);

    const chartReady = ref(false);
    watch(
      [reportLoading, reportLoadError],
      ([loading, error]) => {
        if (!loading && !error) {
          nextTick(() => {
            chartReady.value = true;
          });
        } else {
          chartReady.value = false;
        }
      },
      { immediate: true }
    );

    const reportData = computed(() => {
      const { min, max } = dateRange.value;
      const minKey = toDateKey(min);
      const maxKey = toDateKey(max);
      const bucketDocs = reportDailyStore.getBucketsInRange(minKey, maxKey);
      const merged = mergeReportBuckets(bucketDocs);
      const payToProviderByLash = computePayToProviderByLash(
        appointmentStore.appointments,
        userStore.users,
        minKey,
        maxKey
      );
      return mergedToReportUI(
        merged,
        clientStore.clients,
        serviceStore.services,
        userStore.users,
        paymentMethodStore.paymentMethods,
        payToProviderByLash
      );
    });

    const totals = computed(() => reportData.value.totals);
    const chartCategories = computed(() => reportData.value.chartData.categories);
    const series = computed(() => reportData.value.chartData.series);

    const chartOptions = computed(() => ({
      chart: {
        id: 'report-chart',
        fontFamily: 'var(--font-sans)',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      xaxis: { categories: chartCategories.value },
      colors: ['#b07d7d', '#5a5a5a'],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      legend: { position: 'top' },
      grid: { borderColor: 'var(--color-border)' },
      yaxis: {
        title: { text: 'R$' },
        labels: { formatter: (val) => formatBrl(val) }
      },
      tooltip: {
        y: { formatter: (val) => formatBrl(val) }
      }
    }));

    const dailyValues = computed(() => reportData.value.dailyValues);
    const customerPaidVsExpected = computed(() => reportData.value.customerPaidVsExpected);
    const outstandingByCustomer = computed(() => reportData.value.outstandingByCustomer);
    const revenueByService = computed(() => reportData.value.revenueByService);
    const revenueByTechnician = computed(() => reportData.value.revenueByTechnician);
    const revenueByPaymentMethod = computed(() => reportData.value.revenueByPaymentMethod);

    const outstandingColumns = [
      { key: 'clientName', label: 'Cliente', sortable: true, numeric: false },
      { key: 'expected', label: 'Valor', sortable: true, numeric: true }
    ];

    const technicianColumns = [
      { key: 'lashName', label: 'Lash', sortable: true, numeric: false },
      { key: 'count', label: 'Atendimentos', sortable: true, numeric: true },
      { key: 'expected', label: 'Total', sortable: true, numeric: true },
      { key: 'payToProvider', label: 'A pagar', sortable: true, numeric: true }
    ];

    const dailyColumns = [
      { key: 'date', label: 'Data', sortable: false, numeric: false },
      { key: 'gross', label: 'Bruto', sortable: false, numeric: true },
      { key: 'fee', label: 'Taxa', sortable: false, numeric: true },
      { key: 'net', label: 'Líquido', sortable: false, numeric: true }
    ];

    const paidVsExpectedColumns = [
      { key: 'clientName', label: 'Cliente', sortable: true, numeric: false },
      { key: 'expected', label: 'Esperado', sortable: true, numeric: true },
      { key: 'paid', label: 'Pago', sortable: true, numeric: true },
      {
        key: 'difference',
        label: 'Diferença',
        sortable: true,
        numeric: true,
        cellClass: (row) => row.difference > 0 ? 'report-t-ok' : row.difference < 0 ? 'report-t-warn' : ''
      },
      { key: 'count', label: 'Nº', sortable: true, numeric: true }
    ];

    const serviceColumns = [
      { key: 'serviceName', label: 'Serviço', sortable: true, numeric: false },
      { key: 'count', label: 'Qtd', sortable: true, numeric: true },
      { key: 'gross', label: 'Bruto', sortable: true, numeric: true },
      { key: 'fee', label: 'Taxa', sortable: true, numeric: true },
      { key: 'net', label: 'Líquido', sortable: true, numeric: true }
    ];

    const paymentMethodColumns = [
      { key: 'paymentMethodName', label: 'Forma', sortable: true, numeric: false },
      { key: 'count', label: 'Qtd', sortable: true, numeric: true },
      { key: 'gross', label: 'Bruto', sortable: true, numeric: true },
      { key: 'fee', label: 'Taxa', sortable: true, numeric: true },
      { key: 'net', label: 'Líquido', sortable: true, numeric: true }
    ];

    function formatBrl(n) {
      return Number(n).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
    }

    function formatOutstandingCell(row, col) {
      if (col.key === 'expected') return formatBrl(row[col.key]);
      return row[col.key];
    }

    function formatTechnicianCell(row, col) {
      if (col.key === 'expected' || col.key === 'payToProvider') return formatBrl(row[col.key]);
      return row[col.key];
    }

    function formatDailyCell(row, col) {
      if (col.key === 'date') return formatDateDdMmYy(row[col.key]);
      if (['gross', 'fee', 'net'].includes(col.key)) return formatBrl(row[col.key]);
      return row[col.key];
    }

    function formatPaidVsExpectedCell(row, col) {
      if (['expected', 'paid', 'difference'].includes(col.key)) {
        return formatBrl(row[col.key]);
      }
      return row[col.key];
    }

    function formatServiceCell(row, col) {
      if (['gross', 'fee', 'net'].includes(col.key)) return formatBrl(row[col.key]);
      return row[col.key];
    }

    function formatPaymentMethodCell(row, col) {
      if (['gross', 'fee', 'net'].includes(col.key)) return formatBrl(row[col.key]);
      return row[col.key];
    }

    return {
      periodRange,
      periodRangeOptions,
      customStartDate,
      customEndDate,
      canAccessReport,
      backfillLoading,
      backfillMessage,
      runBackfill,
      reportLoading,
      reportLoadError,
      chartReady,
      openSections,
      sortPaid,
      sortOutstanding,
      sortService,
      sortTechnician,
      sortPaymentMethod,
      setSort,
      totals,
      series,
      chartOptions,
      dailyValues,
      customerPaidVsExpected,
      outstandingByCustomer,
      revenueByService,
      revenueByTechnician,
      revenueByPaymentMethod,
      outstandingColumns,
      technicianColumns,
      dailyColumns,
      paidVsExpectedColumns,
      serviceColumns,
      paymentMethodColumns,
      formatBrl,
      formatOutstandingCell,
      formatTechnicianCell,
      formatDailyCell,
      formatPaidVsExpectedCell,
      formatServiceCell,
      formatPaymentMethodCell
    };
  }
};
</script>

<style scoped>
.report-page {
  padding: var(--space-md) 0;
}

.report-page-header {
  margin-bottom: var(--space-lg);
}

.report-period-column {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-top: var(--space-md);
}

.report-period-label {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.report-period-select {
  padding: var(--space-sm) var(--space-md);
  padding-right: 2rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.9rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%235a5a5a' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
}

.report-period-select:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.report-period-custom {
  flex-wrap: wrap;
}

.report-period-field {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.9rem;
  width: 100%;
}

.report-period-field:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.report-backfill-row {
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
}

.report-backfill-btn {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.875rem;
  cursor: pointer;
}

.report-backfill-btn:hover:not(:disabled) {
  background: var(--color-border);
}

.report-backfill-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.report-backfill-message {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.report-loading-skeleton {
  margin: var(--space-md) 0;
}

.report-loading-skeleton-caption {
  margin: 0 0 var(--space-lg);
  font-size: 0.95rem;
  color: var(--color-text-secondary);
}

.report-skeleton-kpis {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-sm);
  margin-bottom: var(--space-xl);
}

@media (max-width: 560px) {
  .report-skeleton-kpis {
    grid-template-columns: 1fr;
  }
}

.report-skeleton-kpi {
  height: 72px;
  border-radius: var(--radius-md);
}

.report-skeleton-chart {
  height: 240px;
  margin-bottom: var(--space-xl);
  border-radius: var(--radius-md);
}

.report-skeleton-section {
  margin-bottom: var(--space-lg);
}

.report-skeleton-toggle {
  height: 40px;
  margin-bottom: var(--space-xs);
  border-radius: var(--radius-sm);
}

.report-skeleton-table {
  height: 120px;
  border-radius: var(--radius-md);
}

.report-skeleton-block {
  background: var(--color-border);
  opacity: 0.7;
  animation: report-skeleton-pulse 1.2s ease-in-out infinite;
}

@keyframes report-skeleton-pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.85;
  }
}

.report-load-error {
  color: var(--color-error);
  margin: var(--space-md) 0;
  padding: var(--space-md);
  background: var(--color-error-bg);
  border-radius: var(--radius-sm);
}
</style>
